import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  ArrowLeftIcon,
  CopyIcon,
  EditIcon,
  DownloadIcon,
  XIcon,
  CheckIcon,
  HistoryIcon,
  UsersIcon,
  ClipboardListIcon,
  RefreshCwIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Navbar } from "../../components/common/Navbar";
import { EnhancePromptDialog } from "../../components/lesson-plan-builder/EnhancePromptDialog";
import { LessonPlanForm } from "../../components/lesson-plan-builder/LessonPlanForm";
import { InviteCollaboratorDialog } from "../../components/collaboration";
import { CommentSection } from "../../components/comment/CommentSectionLesson";
import { useAuth } from "../../context/AuthContext";
import {
  updateChallengeProgress,
  saveLessonPlan,
  updateLessonPlan,
  LessonPlan,
} from "../../lib/supabase";
import { exportResultToPDF } from "../../lib/exportPdf";
import { getUnifiedAIService } from "../../lib/unifiedAIService";
import { supabase } from "../../lib/supabase";

import { MarkdownRenderer } from "../../components/ui/MarkdownRenderer";
import { GenerateLoadingModal } from "../../components/common/GenerateLoadingModal";

import { marked } from "marked";

type ViewMode = "input" | "result" | "edit";

export const LessonPlanBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUserProfile } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    mataPelajaran: "",
    jenjangPendidikan: "",
    kelas: "",
    kurikulum: "",
    topik: "",
    tujuanPembelajaran: "",
    kriteriaTambahan: "",
  });

  const [viewMode, setViewMode] = useState<ViewMode>("input");

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState<string | null>(null);
  const [generatedTitle, setGeneratedTitle] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState<string>("");
  const [, setStreamingMetadata] = useState<Record<string, unknown> | null>(
    null
  );

  const [editableResult, setEditableResult] = useState<string>("");
  const [editableTitle, setEditableTitle] = useState<string>("");
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const [showEnhancePromptDialog, setShowEnhancePromptDialog] = useState(false);
  const [showInviteCollaboratorDialog, setShowInviteCollaboratorDialog] =
    useState(false);

  const [isTransitioning, setIsTransitioning] = useState(false);

  const [currentLessonPlanId, setCurrentLessonPlanId] = useState<string | null>(
    null
  );

  const [isCommentsLoading, setIsCommentsLoading] = useState(true);

  const [saranaPrasarana, setSaranaPrasarana] = useState({
    proyektor: false,
    internet: false,
    laboratorium: false,
    papanTulisDigital: false,
    komputer: false,
    lcdTv: false,
    perpustakaan: false,
  });
  const [keteranganSarpras, setKeteranganSarpras] = useState<string>("");

  const [gayaBelajar, setGayaBelajar] = useState({
    audio: false,
    visual: false,
    kinestetik: false,
  });

  const [selectedLearningGoals, setSelectedLearningGoals] = useState<string[]>(
    []
  );

  useEffect(() => {
    if (location.state && "lesson_plan" in location.state) {
      const lessonPlan = location.state.lesson_plan as LessonPlan;

      // Split topik_tujuan back into separate fields
      const topikTujuanParts = lessonPlan.topik_tujuan.split(" - ");
      const topik = topikTujuanParts[0] || lessonPlan.topik_tujuan;
      const tujuanPembelajaran = topikTujuanParts.slice(1).join(" - ") || "";

      // Populate form data
      setFormData({
        mataPelajaran: lessonPlan.mata_pelajaran,
        jenjangPendidikan: lessonPlan.jenjang_pendidikan,
        kelas: "",
        topik: topik,
        kriteriaTambahan: lessonPlan.kriteria_tambahan || "",
        kurikulum: "", // Default empty since not stored in database
        tujuanPembelajaran: tujuanPembelajaran,
      });

      // Set generated content
      setGeneratedTitle(lessonPlan.title);
      setGeneratedResult(lessonPlan.content);
      setEditableTitle(lessonPlan.title);
      setEditableResult(lessonPlan.content);
      setIsCommentsLoading(true);

      // Set to edit mode
      setViewMode("edit");
      setEditingItemId(lessonPlan.id);

      // Set lesson plan ID untuk comment section
      setCurrentLessonPlanId(lessonPlan.id);

      // Clear the location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  const handleGoToHistory = () => {
    navigate("/history");
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      // Reset kelas saat jenjang berubah
      if (field === "jenjangPendidikan") {
        return {
          ...prev,
          jenjangPendidikan: value,
          kelas: "",
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleEnhancePrompt = () => {
    setShowEnhancePromptDialog(true);
  };

  const handleCloseEnhancePromptDialog = () => {
    setShowEnhancePromptDialog(false);
  };

  const handleAcceptEnhancedPrompt = (enhancedObjectives: string) => {
    // Parse the enhanced objectives into a list
    const objectivesList = enhancedObjectives
      .split("\n")
      .map(line => line.trim())
      .filter(line => line.length > 0 && !line.match(/^\d+\.$/)) // Remove empty lines and standalone numbers
      .map(line => line.replace(/^\d+\.\s*/, "")) // Remove leading numbers and dots
      .filter(line => line.length > 0);

    // Replace the selectedLearningGoals with the enhanced objectives
    setSelectedLearningGoals(objectivesList);

    // Also update the form data for backward compatibility
    setFormData(prev => ({
      ...prev,
      tujuanPembelajaran: objectivesList.join(", "),
    }));

    toast.success("Tujuan pembelajaran berhasil ditingkatkan!");
  };

  // Mengupdate progress dan notifikasi challenge (Opsional/Background)
  const updateChallengeAndProfile = useCallback(async () => {
    const { getCurrentUser } = await import("../../lib/authUtils");
    const { user: currentUser, error: authError } = await getCurrentUser();

    if (authError || !currentUser) {
      return;
    }

    try {
      const challengeResult = await updateChallengeProgress(
        currentUser.id,
        "lesson_plan_created"
      );

      if (challengeResult.success && challengeResult.data) {
        await refreshUserProfile();

        if (
          challengeResult.data.challenge_completed &&
          challengeResult.data.points_awarded > 0
        ) {
          const challengeNames =
            challengeResult.data.completed_challenges.join(", ");
          toast.success(
            `Tantangan "${challengeNames}" selesai! +${challengeResult.data.points_awarded} poin`,
            { duration: 5000 }
          );
        }
      }
    } catch (error) {
      console.error("💥 Exception updating challenge progress:", error);
    }
  }, [refreshUserProfile]);

  // 🆕 FUNGSI UTAMA BARU: Menyimpan Lesson Plan ke Database secara Synchronous (Awaitable)
  const saveLessonPlanToDb = async (
    title: string,
    content: string
  ): Promise<string | undefined> => {
    const { getCurrentUser } = await import("../../lib/authUtils");
    const { user: currentUser, error: authError } = await getCurrentUser();

    if (authError || !currentUser) {
      console.error("User not authenticated for saving.");
      return;
    }

    // Compose Sarpras into kriteria_tambahan when saving new
    const selectedSarprasForSave = Object.entries(saranaPrasarana)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .map(
        s =>
          (
            ({
              proyektor: "proyektor",
              internet: "internet",
              laboratorium: "laboratorium",
              papanTulisDigital: "papan tulis digital",
              komputer: "komputer/laptop",
              lcdTv: "LCD/TV",
              perpustakaan: "perpustakaan",
            }) as Record<string, string>
          )[s] || s
      )
      .join(", ");

    const sarprasTextForSave = selectedSarprasForSave
      ? `Fasilitas tersedia: ${selectedSarprasForSave}. ${
          keteranganSarpras?.trim()
            ? `Catatan: ${keteranganSarpras.trim()}.`
            : ""
        }`
      : keteranganSarpras?.trim()
        ? `Catatan fasilitas: ${keteranganSarpras.trim()}.`
        : "";

    const selectedStyles = Object.entries(gayaBelajar)
      .filter(([, v]) => v)
      .map(
        ([k]) =>
          (
            ({
              audio: "audio",
              visual: "visual",
              kinestetik: "kinestetik",
            }) as Record<string, string>
          )[k] || k
      )
      .join(", ");

    const gayaBelajarTextForSave = selectedStyles
      ? `Gaya belajar siswa dominan: ${selectedStyles}. Sesuaikan metode dan media agar menekankan preferensi tersebut.`
      : "";

    const combinedKriteriaForSave = [
      formData.kriteriaTambahan?.trim(),
      sarprasTextForSave?.trim(),
      gayaBelajarTextForSave?.trim(),
    ]
      .filter(Boolean)
      .join("\n\n");

    const dataToSave = {
      title: title,
      content: content,
      mata_pelajaran: formData.mataPelajaran,
      jenjang_pendidikan: formData.jenjangPendidikan,
      // Menggabungkan topik dan tujuan pembelajaran seperti yang diharapkan di database
      topik_tujuan: `${formData.topik} - ${
        selectedLearningGoals.length > 0
          ? selectedLearningGoals.join(", ")
          : formData.tujuanPembelajaran
      }`,
      kriteria_tambahan: combinedKriteriaForSave,
    };

    if (content.length < 50) {
      console.error("Content is too short, aborting save to database.");
      throw new Error("Konten hasil generate terlalu pendek (gagal dari AI).");
    }

    try {
      const saveResult = await saveLessonPlan(currentUser.id, dataToSave);

      if (saveResult.success && saveResult.data?.id) {
        return saveResult.data.id;
      } else {
        console.error("❌ Failed to save lesson plan:", saveResult.error);
        throw new Error(
          saveResult.error ||
            "Gagal menyimpan rencana pembelajaran ke database."
        );
      }
    } catch (error) {
      console.error("💥 Exception saving lesson plan:", error);
      throw error;
    }
  };

  const handleGenerateNow = async () => {
    // 1. VALIDASI
    // Validasi diperluas untuk semua field yang dibutuhkan
    // Di mode edit, izinkan regenerate tanpa wajib mengisi kelas & kurikulum
    const requireKelasKurikulum = viewMode !== "edit";
    const wasEditMode = viewMode === "edit";
    const isMissing =
      !formData.mataPelajaran ||
      !formData.jenjangPendidikan ||
      (requireKelasKurikulum && (!formData.kelas || !formData.kurikulum)) ||
      !formData.topik ||
      (!formData.tujuanPembelajaran && selectedLearningGoals.length === 0);
    if (isMissing) {
      toast.error("Mohon lengkapi semua field yang diperlukan");
      return;
    }

    // 2. SETUP LOADING STATE (Initial)
    setIsGenerating(true);
    setGeneratedResult(null);
    setGeneratedTitle(null);
    // Jangan kosongkan editor saat sedang di mode edit, agar user tetap melihat konten lama sampai hasil baru masuk
    if (!wasEditMode) {
      setEditableResult("");
      setEditableTitle("");
    }
    setProgress(0);
    setIsStreaming(false);
    setStreamedContent("");
    setStreamingMetadata(null);
    // JANGAN reset currentLessonPlanId agar regenerate memperbarui entri yang sama
    // Jika bukan mode edit, pindah ke "result" agar loading spinner muncul di kanan.
    if (!wasEditMode) {
      setViewMode("result");
    }

    // Handle scroll behavior (sama seperti sebelumnya)
    setTimeout(() => {
      const isMobile = window.innerWidth < 1024;
      if (isMobile) {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, 100);

    let progressInterval: NodeJS.Timeout | null = null;
    const aiService = getUnifiedAIService();
    const providerStatus = aiService.getProviderStatus();

    // Sanitize AI content: forbid recording/podcast for Audio, remove unwanted/duplicate headings, normalize stray characters
    const sanitizeAIContent = (text: string): string => {
      if (!text) return text;
      let out = text;
      // Replace forbidden audio terms
      const forbiddenAudio = [/rekaman audio/gi, /podcast/gi, /recording/gi];
      for (const pat of forbiddenAudio) {
        out = out.replace(pat, "penjelasan lisan langsung oleh guru");
      }
      // Remove non-ASCII anomalies like stray CJK in Indonesian words
      out = out.replace(/[\u0100-\uFFFF]/g, "");
      // Remove entire 3A section if present
      // Case 1: Markdown heading '## 3A. RINGKASAN GAYA BELAJAR' until next '##' or end
      const removeSectionMd = (label: string) => {
        const re = new RegExp(
          `^##\\s*${label}\\s*$[\\s\\S]*?(?=^##\\s+|$)`,
          "gmi"
        );
        out = out.replace(re, "");
      };
      removeSectionMd("3A\\. RINGKASAN GAYA BELAJAR");
      // Case 2: Plain-text heading '3A. RINGKASAN GAYA BELAJAR' (no ##) until next numbered or markdown heading, or end
      const rePlain3A = new RegExp(
        `^(?:##\\s*)?3A\\.\\s*RINGKASAN GAYA BELAJAR\\s*$[\\s\\S]*?(?=^(?:##\\s+|[0-9]+\\.\\s)|$)`,
        "gmi"
      );
      out = out.replace(rePlain3A, "");
      // Cleanup any leftover single line mentioning 3A heading without content
      out = out.replace(
        /^\s*(?:##\s*)?3A\.\s*RINGKASAN GAYA BELAJAR\s*$/gim,
        ""
      );
      // Deduplicate single-line headings (keep first) for section 9
      const dedupeHeading = (label: string) => {
        const re = new RegExp(`^##\\s*${label}\\s*$`, "gmi");
        let count = 0;
        out = out.replace(re, m => {
          count += 1;
          return count === 1 ? m : "";
        });
      };
      dedupeHeading("9\\. PENYESUAIAN GAYA BELAJAR");

      // Always remove any existing Section 9 completely
      const section9Regex =
        /^##\s*9\.\s*PENYESUAIAN GAYA BELAJAR\s*$[\s\S]*?(?=^##\s+|$)/gim;
      out = out.replace(section9Regex, "");

      // Remove unselected styles subsections inside section 9
      const removeStyleBlock = (
        styleLabel: "Audio" | "Visual" | "Kinestetik",
        keep: boolean
      ) => {
        if (keep) return;
        const re = new RegExp(
          // start at a line like "Audio:" / "Visual:" / "Kinestetik:" (with optional leading spaces) and capture until next style label or next markdown heading or end
          `^\\s*${styleLabel}\\s*:(?:\r?\n|\n)[\\s\\S]*?(?=^\\s*(?:Audio|Visual|Kinestetik)\\s*:|^##\\s+|$)`,
          "gmi"
        );
        out = out.replace(re, "");
      };
      removeStyleBlock("Audio", !!gayaBelajar.audio);
      removeStyleBlock("Visual", !!gayaBelajar.visual);
      removeStyleBlock("Kinestetik", !!gayaBelajar.kinestetik);

      // Explicitly clean Section 8 so it only shows Remedial & Pengayaan
      const section8Regex =
        /^(?:##\s*)?8\.\s*PROGRAM REMEDIAL DAN PENGAYAAN\s*$[\s\S]*?(?=^##\s+|^\d+\.\s|$)/gim;
      out = out.replace(section8Regex, block => {
        let b = block;
        // Remove style blocks inside section 8
        b = b.replace(
          /^(?:\s*)(Audio|Visual|Kinestetik)\s*:(?:\r?\n|\n)[\s\S]*?(?=^\s*(?:Audio|Visual|Kinestetik)\s*:|^##\s+|^\d+\.\s|$)/gim,
          ""
        );
        // Remove single lines mentioning dominant styles
        b = b.replace(/^.*Gaya belajar siswa dominan:.*$/gim, "");
        // Remove inline tagged lines inside section 8 if any
        b = b.replace(/^.*\[(Audio|Visual|Kinestetik)\].*$/gim, "");
        // Tidy extra blank lines
        b = b.replace(/\n{3,}/g, "\n\n");
        return b;
      });

      // Remove any style blocks (Audio/Visual/Kinestetik) OUTSIDE Section 9 to avoid duplicates after point 8
      // Protect Section 9 by splitting the document around it
      const splitAroundSection9 = () => {
        const m = out.match(section9Regex);
        if (!m) return { pre: out, sec9: "", post: "" };
        const sec9 = m[0];
        const parts = out.split(section9Regex);
        // When split with global regex, parts may contain multiple segments; we assume single section 9
        const pre = parts[0] ?? "";
        const post = parts[1] ?? "";
        return { pre, sec9, post };
      };
      const rmStyleBlocks = (s: string) => {
        const reAll =
          /^(?:\s*)(Audio|Visual|Kinestetik)\s*:(?:\r?\n|\n)[\s\S]*?(?=^\s*(?:Audio|Visual|Kinestetik)\s*:|^##\s+|^(?:\d+\.\s)|$)/gim;
        return s.replace(reAll, "");
      };
      const { pre, sec9, post } = splitAroundSection9();
      const removeDominantLine = (s: string) =>
        s.replace(/^.*Gaya belajar siswa dominan:.*$/gim, "");
      if (sec9) {
        out = `${removeDominantLine(
          rmStyleBlocks(pre)
        )}${sec9}${removeDominantLine(rmStyleBlocks(post))}`;
      } else {
        out = removeDominantLine(rmStyleBlocks(out));
      }

      // Strip inline tags for unselected styles, keep selected as-is
      const stripTag = (
        label: "Audio" | "Visual" | "Kinestetik",
        keep: boolean
      ) => {
        if (keep) return;
        // Remove entire lines that contain the unselected tag to avoid leftover trailing text
        const lineWithTag = new RegExp(`^.*\\[${label}\\].*$`, "gmi");
        out = out.replace(lineWithTag, "");
        const re = new RegExp(`\\[${label}\\]`, "gi");
        out = out.replace(re, "");
        // For combos, remove the unselected token by simplifying common patterns
        out = out
          .replace(/\[Audio\/Kinestetik\]/gi, "[Audio]")
          .replace(/\[Visual\/Kinestetik\]/gi, "[Visual]")
          .replace(/\[Kinestetik\/Audio\]/gi, "[Audio]")
          .replace(/\[Kinestetik\/Visual\]/gi, "[Visual]");
        // If after simplification a bracket becomes empty, remove leftover brackets
        out = out.replace(/\[\s*\]/g, "");
      };
      stripTag("Audio", !!gayaBelajar.audio);
      stripTag("Visual", !!gayaBelajar.visual);
      stripTag("Kinestetik", !!gayaBelajar.kinestetik);

      // Tidy excessive blank lines after removals
      out = out.replace(/\n{3,}/g, "\n\n");
      return out;
    };

    try {
      let finalContent: string | null = null;
      let finalTitle: string | null = null;
      let finalProvider: string = "AI";
      let isFallback = false;

      // Start progress simulation (for non-streaming)
      if (!providerStatus.streamingEnabled) {
        progressInterval = setInterval(() => {
          setProgress(prev => {
            if (prev >= 85) {
              if (progressInterval) clearInterval(progressInterval);
              return 85;
            }
            return prev + Math.random() * 10;
          });
        }, 300);
      }

      // Compose Sarpras summary for AI prompt
      const selectedSarpras = Object.entries(saranaPrasarana)
        .filter(([, v]) => v)
        .map(([k]) => k)
        .map(
          s =>
            (
              ({
                proyektor: "proyektor",
                internet: "internet",
                laboratorium: "laboratorium",
                papanTulisDigital: "papan tulis digital",
                komputer: "komputer/laptop",
                lcdTv: "LCD/TV",
                perpustakaan: "perpustakaan",
              }) as Record<string, string>
            )[s] || s
        )
        .join(", ");

      const sarprasText = selectedSarpras
        ? `Fasilitas tersedia: ${selectedSarpras}. ${
            keteranganSarpras?.trim()
              ? `Catatan: ${keteranganSarpras.trim()}.`
              : ""
          }`
        : keteranganSarpras?.trim()
          ? `Catatan fasilitas: ${keteranganSarpras.trim()}.`
          : "";

      const selectedStylesForPrompt = Object.entries(gayaBelajar)
        .filter(([, v]) => v)
        .map(
          ([k]) =>
            (
              ({
                audio: "audio",
                visual: "visual",
                kinestetik: "kinestetik",
              }) as Record<string, string>
            )[k] || k
        )
        .join(", ");

      const gayaBelajarText = selectedStylesForPrompt
        ? `Gaya belajar siswa dominan: ${selectedStylesForPrompt}.

Instruksi wajib:
- Di bagian "Metode dan Pendekatan", "Kegiatan Pembelajaran" (Pembukaan/Inti/Penutup), serta "Media dan Sumber Belajar", beri penanda pada butir yang relevan dengan tag [Audio]/[Visual]/[Kinestetik].`
        : "";

      const combinedKriteria = [
        formData.kriteriaTambahan?.trim(),
        sarprasText?.trim(),
        gayaBelajarText?.trim(),
      ]
        .filter(Boolean)
        .join("\n\n");

      // 3. PANGGIL AI SERVICE
      const result = await aiService.generateLessonPlan({
        mataPelajaran: formData.mataPelajaran,
        // Sertakan kelas ke dalam konteks jenjang untuk AI (tanpa mengubah data DB)
        jenjangPendidikan: formData.kelas
          ? `${formData.jenjangPendidikan} ${formData.kelas}`
          : formData.jenjangPendidikan,
        kurikulum: formData.kurikulum,
        topik: formData.topik,
        tujuanPembelajaran:
          selectedLearningGoals.length > 0
            ? selectedLearningGoals.join(", ")
            : formData.tujuanPembelajaran,
        kriteriaTambahan: combinedKriteria,
      });

      // Clear progress interval if it was set
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      // 4. LOGIKA FINAL SETELAH AI SELESAI
      if (result.success) {
        // Tentukan konten dan judul final
        finalContent = finalContent || sanitizeAIContent(result.content || "");
        finalTitle =
          finalTitle ||
          result.title ||
          generatedTitle ||
          `Rencana Pembelajaran ${formData.mataPelajaran} - ${(formData.kelas
            ? `${formData.jenjangPendidikan} ${formData.kelas}`
            : formData.jenjangPendidikan
          )?.toUpperCase()}`;
        finalProvider = result.provider || finalProvider;
        isFallback = isFallback || result.fallback || false;

        if (!finalContent) {
          throw new Error("Konten hasil generate kosong.");
        }

        // 🌟 SIMPAN/UPDATE KE DATABASE
        const { getCurrentUser } = await import("../../lib/authUtils");
        const { user: currentUser, error: authError } = await getCurrentUser();

        if (authError || !currentUser) {
          throw new Error("Sesi tidak valid. Silakan login kembali.");
        }

        const targetId = editingItemId ?? currentLessonPlanId;
        if (targetId) {
          // UPDATE entri existing untuk mencegah duplikasi history
          const dataToUpdate = {
            title: finalTitle,
            content: finalContent,
            mata_pelajaran: formData.mataPelajaran,
            jenjang_pendidikan: formData.jenjangPendidikan,
            // Menggabungkan topik dan tujuan pembelajaran seperti yang diharapkan di database
            topik_tujuan: `${formData.topik} - ${
              selectedLearningGoals.length > 0
                ? selectedLearningGoals.join(", ")
                : formData.tujuanPembelajaran
            }`,
            kriteria_tambahan: combinedKriteria,
          };

          const updateResult = await updateLessonPlan(
            currentUser.id,
            targetId,
            dataToUpdate
          );
          if (!updateResult.success) {
            throw new Error(
              updateResult.error || "Gagal memperbarui rencana pembelajaran."
            );
          }

          // Set state tampilan final
          setGeneratedTitle(finalTitle);
          setGeneratedResult(finalContent);
          setEditableTitle(finalTitle);
          setEditableResult(finalContent);
          setEditingItemId(targetId);
          setCurrentLessonPlanId(targetId);
        } else {
          // CREATE entri baru jika belum ada ID
          const newLessonPlanId = await saveLessonPlanToDb(
            finalTitle,
            finalContent
          );

          if (newLessonPlanId) {
            setCurrentLessonPlanId(newLessonPlanId);
            setEditingItemId(newLessonPlanId);

            setGeneratedTitle(finalTitle);
            setGeneratedResult(finalContent);
            setEditableTitle(finalTitle);
            setEditableResult(finalContent);
          } else {
            throw new Error(
              "Gagal mendapatkan ID setelah penyimpanan database."
            );
          }
        }

        // Set generating to false immediately after successful generation
        setIsGenerating(false);

        // Jika bukan dari mode edit, pindahkan ke tampilan hasil untuk konsistensi
        if (!wasEditMode) {
          setViewMode("result");
        }

        // Update challenges di background
        updateChallengeAndProfile();

        // Tampilkan toast sukses jika non-streaming (toast streaming sudah di handle di onStreamComplete)
        if (!providerStatus.streamingEnabled) {
          const toastMessage = isFallback
            ? `Rencana pembelajaran berhasil dibuat menggunakan template!`
            : `Rencana pembelajaran berhasil dibuat dengan ${
                finalProvider === "sumpod-openai" ? "SumoPod AI" : finalProvider
              }!`;
          toast.success(toastMessage, { duration: 4000 });
        }
      } else {
        throw new Error(result.error || "Failed to generate lesson plan");
      }
    } catch (error) {
      // Send error to Sentry with context
      Sentry.captureException(error, {
        tags: {
          component: "LessonPlanBuilder",
          action: "handleGenerateNow",
        },
        contexts: {
          form_data: {
            mataPelajaran: formData.mataPelajaran,
            jenjangPendidikan: formData.jenjangPendidikan,
            kelas: formData.kelas,
            kurikulum: formData.kurikulum,
            topik: formData.topik,
          },
        },
      });
      
      setProgress(0);
      setIsStreaming(false);
      setStreamedContent("");
      setCurrentLessonPlanId(null); // Pastikan ID di-reset jika gagal
      setIsGenerating(false); // Ensure loading state is reset on error

      // Karena gagal menyimpan atau generate, kita reset UI agar user bisa coba lagi.
      // Kita kembalikan ke mode input.
      setViewMode("input");

      toast.error(
        "Gagal membuat atau menyimpan rencana pembelajaran. Silakan coba lagi.",
        {
          duration: 4000,
        }
      );
    } finally {
      // 6. CLEANUP AKHIR
      setIsGenerating(false);
    }
  };

  const handleAssessment = () => {
    // Validate that lesson plan is generated
    if (!generatedResult) {
      toast.error("Harap generate lesson plan terlebih dahulu");
      return;
    }

    // Validate required form fields
    const requiredFields = [
      {
        field: "mataPelajaran" as keyof typeof formData,
        label: "Mata Pelajaran",
      },
      {
        field: "jenjangPendidikan" as keyof typeof formData,
        label: "Jenjang Pendidikan",
      },
      { field: "kelas" as keyof typeof formData, label: "Kelas" },
      { field: "topik" as keyof typeof formData, label: "Topik" },
      {
        field: "tujuanPembelajaran" as keyof typeof formData,
        label: "Tujuan Pembelajaran",
      },
    ];

    const missingFields = requiredFields.filter(({ field }) => {
      // Special handling for tujuanPembelajaran: check both formData and selectedLearningGoals
      if (field === "tujuanPembelajaran") {
        return (
          !formData[field]?.trim() && selectedLearningGoals.length === 0
        );
      }
      return !formData[field]?.trim();
    });

    if (missingFields.length > 0) {
      const missingLabels = missingFields.map(({ label }) => label).join(", ");
      toast.error(`Data tidak lengkap: ${missingLabels}`);
      return;
    }

    // Get tujuanPembelajaran from either formData or selectedLearningGoals
    const tujuanPembelajaran =
      selectedLearningGoals.length > 0
        ? selectedLearningGoals.join(", ")
        : formData.tujuanPembelajaran;

    // Navigate to assessment builder with complete context from current lesson plan
    navigate("/assessment-builder", {
      state: {
        fromLessonPlan: true,
        mataPelajaran: formData.mataPelajaran,
        jenjangPendidikan: formData.jenjangPendidikan,
        kelas: formData.kelas,
        topik: formData.topik,
        tujuanPembelajaran: tujuanPembelajaran,
        kriteriaTambahan: generatedResult,
      },
    });

    toast.success("Navigating to Assessment Builder...");
  };

  const handleCopy = async () => {
    if (generatedResult) {
      const rawContent = generatedTitle
        ? `## ${generatedTitle}\n\n${generatedResult}` // pakai heading markdown
        : generatedResult;

      // Konversi markdown ke HTML
      const htmlContent = await marked(rawContent);

      // Buat versi teks biasa (fallback)
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent as string;
      const plainText = tempDiv.innerText;

      try {
        // Gunakan Clipboard API untuk menulis HTML + teks
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([htmlContent as string], {
              type: "text/html",
            }),
            "text/plain": new Blob([plainText], { type: "text/plain" }),
          }),
        ]);

        toast.success("Konten berhasil disalin dengan format!");
      } catch (err) {
        console.error("Clipboard error:", err);
        toast.error("Gagal menyalin dengan format");
      }
    }
  };

  const handleEdit = () => {
    if (generatedResult && generatedTitle) {
      setIsTransitioning(true);

      setTimeout(() => {
        setEditableResult(generatedResult);
        setEditableTitle(generatedTitle);

        if (!editingItemId && currentLessonPlanId) {
          setEditingItemId(currentLessonPlanId);
        }

        setViewMode("edit");

        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 150);
    }
  };

  const handleSaveEdit = async () => {
    // 1. Validasi awal
    if (!user?.id) {
      toast.error("User tidak terautentikasi.");
      return;
    }

    if (
      !editableTitle.trim() ||
      !editableResult.trim() ||
      !formData.mataPelajaran.trim() ||
      !formData.jenjangPendidikan.trim() ||
      !formData.topik.trim() ||
      !formData.tujuanPembelajaran.trim()
    ) {
      toast.error("Semua field wajib diisi!");
      return;
    }

    // 2. Cek apakah ini mode UPDATE atau SAVE AS NEW
    if (editingItemId || currentLessonPlanId) {
      // MODE: UPDATE EXISTING LESSON PLAN (pastikan ID valid)
      setIsTransitioning(true);

      const targetId = editingItemId ?? currentLessonPlanId;
      if (!targetId) {
        setIsTransitioning(false);
        toast.error(
          "ID lesson plan tidak ditemukan. Simpan/generate terlebih dahulu."
        );
        return;
      }

      const dataToUpdate = {
        title: editableTitle.trim(),
        content: editableResult.trim(),
        mata_pelajaran: formData.mataPelajaran,
        jenjang_pendidikan: formData.jenjangPendidikan,
        topik_tujuan: `${formData.topik} - ${
          selectedLearningGoals.length > 0
            ? selectedLearningGoals.join(", ")
            : formData.tujuanPembelajaran
        }`,
        kriteria_tambahan: formData.kriteriaTambahan,
      };

      const loadingToast = toast.loading("Menyimpan perubahan...");
      try {
        const updateResult = await updateLessonPlan(
          user.id,
          targetId,
          dataToUpdate
        );

        toast.dismiss(loadingToast);

        if (updateResult.success) {
          // Sinkronkan state agar UI konsisten
          setGeneratedResult(editableResult.trim());
          setGeneratedTitle(editableTitle.trim());

          toast.success("Rencana pembelajaran berhasil diperbarui!");
          navigate("/history", { replace: true });
        } else {
          console.error("❌ Update failed:", updateResult.error);
          toast.error(
            updateResult.error || "Gagal memperbarui rencana pembelajaran."
          );
        }
      } catch (error) {
        console.error("💥 Exception saat update:", error);
        toast.dismiss(loadingToast);
        toast.error("Terjadi kesalahan teknis saat memperbarui data.");
      } finally {
        setIsTransitioning(false);
      }
    } else {
      // MODE: SAVE AS NEW (ketika tidak ada editingItemId)
      try {
        setIsTransitioning(true);

        // Update state lokal
        setGeneratedResult(editableResult.trim());
        setGeneratedTitle(editableTitle.trim());

        // Kembali ke mode result
        await new Promise(resolve => setTimeout(resolve, 150));
        setViewMode("result");

        toast.success("Perubahan berhasil disimpan!");

        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (error) {
        console.error("💥 Exception during save:", error);
        toast.error("Gagal menyimpan perubahan.");
      } finally {
        setIsTransitioning(false);
      }
    }
  };

  const handleCancelEdit = () => {
    if (editingItemId) {
      navigate("/history");
    } else {
      setIsTransitioning(true);

      setTimeout(() => {
        setEditableResult("");
        setEditableTitle("");
        setViewMode("result");
        toast("Perubahan dibatalkan");

        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 150);
    }
  };

  const handleExport = async () => {
    if (generatedResult && generatedTitle) {
      try {
        await exportResultToPDF(generatedResult, generatedTitle);
        toast.success("PDF berhasil diunduh dengan format yang rapi!");
      } catch (error) {
        console.error("Error exporting PDF:", error);
        toast.error("Gagal mengekspor PDF");
      }
    }
  };

  const handleReGenerate = async () => {
    // Validate that we have the necessary form data
    if (
      !formData.mataPelajaran ||
      !formData.jenjangPendidikan ||
      !formData.topik ||
      (!formData.tujuanPembelajaran && selectedLearningGoals.length === 0)
    ) {
      toast.error("Data form tidak lengkap untuk regenerasi");
      return;
    }

    // Show confirmation toast
    toast("Regenerating lesson plan with same parameters...", {
      duration: 2000,
    });

    // Track regeneration usage
    // Reuse existing generation logic
    await handleGenerateNow();
  };

  const handleInviteCollaborator = () => {
    setShowInviteCollaboratorDialog(true);
  };

  const handleCloseInviteCollaboratorDialog = () => {
    setShowInviteCollaboratorDialog(false);
  };

  const handleInviteFriend = async (friendId: string) => {
    // Pastikan lesson plan sudah disimpan dan memiliki ID
    if (!currentLessonPlanId) {
      toast.error("Mohon simpan rencana pembelajaran terlebih dahulu.");
      return;
    }

    const { getCurrentUser } = await import("../../lib/authUtils");
    const { user: currentUser, error: authError } = await getCurrentUser();

    if (authError || !currentUser) {
      toast.error("Sesi tidak valid. Silakan login kembali.");
      return;
    }

    try {
      // 1. Dapatkan judul dokumen dari tabel lesson_plans
      const { data: doc, error: docError } = await supabase
        .from("lesson_plans")
        .select("title")
        .eq("id", currentLessonPlanId)
        .single();

      if (docError) {
        throw docError;
      }

      // 2. Masukkan notifikasi ke tabel notifications secara langsung
      // Status: 'pending' (default)
      // Metadata: Simpan ID Pengirim agar bisa dilacak di RLS (Opsional, tapi aman)
      const { error: notifError } = await supabase
        .from("notifications")
        .insert({
          user_id: friendId, // ID user yang diundang (penerima notifikasi)
          type: "collaboration_invite",
          title: "Undangan Kolaborasi Lesson Plan Baru",
          content: `Anda diundang untuk berkolaborasi pada materi "${doc.title}"`,
          related_entity_id: currentLessonPlanId, // ID Lesson Plan
          related_entity_type: "lesson_plan",
          action_status: "pending", // Status awal undangan
          metadata: {
            // Simpan ID inviter, jika diperlukan di masa depan
            inviter_id: currentUser.id,
          },
        });

      if (notifError) {
        throw notifError;
      }

      toast.success("Undangan kolaborasi berhasil dikirim!");
      return true;
    } catch (error) {
      console.error("Terjadi kesalahan saat mengundang kolaborator:", error);
      toast.error(
        "Gagal mengirim undangan kolaborasi. Periksa konsol untuk detailnya."
      );
      return false;
    }
  };

  // Perbaikan: Menyederhanakan dan memastikan sintaks `isFormValid` benar
  const isFormValid =
    formData.mataPelajaran.trim() !== "" &&
    formData.jenjangPendidikan.trim() !== "" &&
    formData.kelas.trim() !== "" &&
    formData.kurikulum.trim() !== "" && // Tambahkan kurikulum
    formData.topik.trim() !== "" &&
    (formData.tujuanPembelajaran.trim() !== "" ||
      selectedLearningGoals.length > 0); // Tambahkan tujuanPembelajaran atau selectedLearningGoals

  return (
    <div className="bg-[#eeeff6] w-full min-h-screen flex flex-col">
      <Navbar />

      <main className="pt-[70px] px-4 sm:px-6 pb-6 flex-1">
        <div className="w-full mx-auto mt-8 bg-white rounded-xl border-b border-[#f0eff0] relative p-4 sm:p-8 min-h-[700px] overflow-hidden">
          <div className="relative z-10 h-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleBackToDashboard}
                  variant="ghost"
                  className="flex items-center gap-2 text-[#373e46] hover:bg-gray-100 p-2 rounded-lg w-auto"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </Button>
                <span className="font-medium text-base sm:text-lg text-[#373e46]">
                  {editingItemId ? "Edit Lesson Plan" : "Lesson Plan Builder"}
                </span>
              </div>

              <Button
                onClick={handleGoToHistory}
                variant="outline"
                className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-xl font-medium transition-all duration-300"
              >
                <HistoryIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Riwayat</span>
              </Button>
            </div>

            {/* Main Layout Container */}
            <div className="flex gap-4 sm:gap-8 h-full transition-all duration-500 ease-in-out flex-col lg:flex-row">
              {/* Left Column */}
              <div
                className={`transition-all duration-500 ease-in-out ${
                  viewMode === "input"
                    ? "w-full max-w-2xl mx-auto"
                    : "w-full lg:w-1/2"
                }`}
              >
                {viewMode === "edit" ? (
                  <div className="h-full flex flex-col">
                    <h2 className="text-lg sm:text-xl font-semibold text-[#373e46] mb-4 sm:mb-6">
                      Result
                    </h2>
                    {isGenerating ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <GenerateLoadingModal isOpen={true} />
                      </div>
                    ) : (
                      <div className="overflow-y-auto p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4 sm:mb-6 max-h-[400px] sm:max-h-[500px]">
                        {generatedTitle && (
                          <h3 className="text-lg sm:text-xl font-bold text-[#373e46] mb-4 pb-3 border-b border-gray-300">
                            {generatedTitle}
                          </h3>
                        )}
                        <div className="relative">
                          <MarkdownRenderer
                            content={generatedResult || ""}
                            className="text-sm sm:text-base"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <LessonPlanForm
                    formData={formData}
                    handleInputChange={handleInputChange}
                    isFormValid={isFormValid}
                    isGenerating={isGenerating}
                    progress={progress}
                    handleGenerateNow={handleGenerateNow}
                    handleEnhancePrompt={handleEnhancePrompt}
                    saranaPrasarana={saranaPrasarana}
                    setSaranaPrasarana={setSaranaPrasarana}
                    keteranganSarpras={keteranganSarpras}
                    setKeteranganSarpras={setKeteranganSarpras}
                    gayaBelajar={gayaBelajar}
                    setGayaBelajar={setGayaBelajar}
                    selectedLearningGoals={selectedLearningGoals}
                    setSelectedLearningGoals={setSelectedLearningGoals}
                  />
                )}
              </div>

              {/* Right Column */}
              <div
                className={`transition-all duration-500 ease-in-out flex flex-col ${
                  viewMode === "input"
                    ? "w-0 opacity-0 overflow-hidden lg:block hidden"
                    : "w-full lg:w-1/2 opacity-100"
                }`}
              >
                {viewMode === "result" && isGenerating ? (
                  /* Show loading modal when generating */
                  <div className="w-full h-full flex items-center justify-center">
                    <GenerateLoadingModal isOpen={true} />
                  </div>
                ) : viewMode === "result" &&
                  (generatedResult || isStreaming || streamedContent) ? (
                  <div
                    className={`w-full flex flex-col h-full transition-all duration-300 ease-in-out ${
                      isTransitioning
                        ? "opacity-0 transform translate-x-4"
                        : "opacity-100 transform translate-x-0"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <div className="flex items-center gap-3">
                        <h2 className="text-lg sm:text-xl font-semibold text-[#373e46]">
                          Result
                        </h2>
                      </div>
                    </div>

                    <div className="overflow-y-auto p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4 sm:mb-6 max-h-[400px] sm:max-h-[1200px]">
                      {generatedTitle && (
                        <h3 className="text-lg sm:text-xl font-bold text-[#373e46] mb-4 pb-3 border-b border-gray-300">
                          {generatedTitle}
                        </h3>
                      )}
                      <div className="relative">
                        <MarkdownRenderer
                          content={generatedResult || ""}
                          className="text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    {/* Re-Generate Button - Full width with Figma design */}
                    <div className="mb-4">
                      <Button
                        onClick={handleReGenerate}
                        disabled={isGenerating}
                        className="w-full h-12 rounded-[24px] bg-[#EFE8FF] border-2 border-transparent bg-clip-padding relative overflow-hidden transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          background:
                            "linear-gradient(#EFE8FF, #EFE8FF) padding-box, linear-gradient(90deg, #6A2CFE 0%, #010FFE 100%) border-box",
                        }}
                      >
                        <div className="flex items-center justify-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-[#6A2CFE] to-[#010FFE] font-semibold text-base">
                          <RefreshCwIcon
                            className={`w-5 h-5 text-[#6A2CFE] ${
                              isGenerating ? "animate-spin" : ""
                            }`}
                          />
                          {isGenerating ? "Regenerating..." : "Re-Generate"}
                        </div>
                      </Button>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                      <Button
                        onClick={handleAssessment}
                        className="flex items-center justify-center gap-2 h-10 sm:h-auto text-[#EEEEF3] font-medium hover:opacity-90 hover:scale-105 transition-all duration-300"
                        style={{
                          background:
                            "linear-gradient(103.96deg, #992CFE 21.04%, #D623FE 90.07%)",
                        }}
                      >
                        <ClipboardListIcon className="w-4 h-4" />
                        Assessment
                      </Button>
                      <Button
                        onClick={handleExport}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 h-10 sm:h-auto"
                      >
                        <DownloadIcon className="w-4 h-4" />
                        Export
                      </Button>
                      <Button
                        onClick={handleEdit}
                        className="flex items-center justify-center gap-2 h-10 sm:h-auto hover:opacity-90 hover:scale-105 transition-all duration-300"
                        style={{
                          background: "#EEEEF3",
                          color: "#37363E",
                        }}
                      >
                        <EditIcon className="w-4 h-4" />
                        Edit
                      </Button>
                      <Button
                        onClick={handleInviteCollaborator}
                        className="flex items-center justify-center gap-2 h-10 sm:h-auto hover:opacity-90 hover:scale-105 transition-all duration-300"
                        style={{
                          background: "transparent",
                          color: "#6b2cfe",
                          border: "1px solid #6b2cfe",
                        }}
                      >
                        <UsersIcon className="w-4 h-4" />
                        Undang Kolaborasi
                      </Button>
                    </div>

                    {/* TAMBAHAN: Comment Section */}
                    {currentLessonPlanId && (
                      <CommentSection lessonPlanId={currentLessonPlanId} />
                    )}
                  </div>
                ) : viewMode === "edit" ? (
                  <div
                    className={`w-full flex flex-col h-full transition-all duration-300 ease-in-out ${
                      isTransitioning
                        ? "opacity-0 transform translate-x-4"
                        : "opacity-100 transform translate-x-0"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <h2 className="text-lg sm:text-xl font-semibold text-[#373e46]">
                        Edit Rencana
                      </h2>
                      <Button
                        onClick={handleCancelEdit}
                        variant="ghost"
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <XIcon className="w-5 h-5 text-gray-500" />
                      </Button>
                    </div>

                    <div className="flex-1 flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-[#373e46]">
                          Judul
                        </label>
                        <Input
                          value={editableTitle}
                          onChange={e => setEditableTitle(e.target.value)}
                          className="h-10 sm:h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white"
                          placeholder="Masukkan judul"
                        />
                      </div>

                      <div className="space-y-2 flex-1 flex flex-col">
                        <label className="block text-sm font-medium text-[#373e46]">
                          Konten
                        </label>
                        <Textarea
                          value={editableResult}
                          onChange={e => setEditableResult(e.target.value)}
                          className="flex-1 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 py-3 resize-none bg-white min-h-[300px] sm:min-h-[400px]"
                          placeholder="Masukkan konten"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <Button
                        onClick={handleReGenerate}
                        disabled={isGenerating}
                        className="w-full h-12 rounded-[24px] bg-[#EFE8FF] border-2 border-transparent bg-clip-padding relative overflow-hidden transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          background:
                            "linear-gradient(#EFE8FF, #EFE8FF) padding-box, linear-gradient(90deg, #6A2CFE 0%, #010FFE 100%) border-box",
                        }}
                      >
                        <div className="flex items-center justify-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-[#6A2CFE] to-[#010FFE] font-semibold text-base">
                          <RefreshCwIcon
                            className={`w-5 h-5 text-[#6A2CFE] ${
                              isGenerating ? "animate-spin" : ""
                            }`}
                          />
                          {isGenerating ? "Regenerating..." : "Re-Generate"}
                        </div>
                      </Button>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                      <Button
                        onClick={handleCopy}
                        variant="outline"
                        className="flex items-center justify-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 h-10 sm:h-auto"
                      >
                        <CopyIcon className="w-4 h-4" />
                        Copy
                      </Button>
                      <Button
                        onClick={handleExport}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 h-10 sm:h-auto"
                      >
                        <DownloadIcon className="w-4 h-4" />
                        Export
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        className="flex items-center justify-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 h-10 sm:h-auto"
                      >
                        <XIcon className="w-4 h-4" />
                        {editingItemId ? "Kembali" : "Cancel"}
                      </Button>

                      <Button
                        onClick={handleSaveEdit}
                        // MODIFIKASI: Tambahkan properti `disabled`
                        disabled={isCommentsLoading || isTransitioning}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg transition-all duration-300 h-10 sm:h-auto disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckIcon className="w-4 h-4" />
                        {editingItemId ? "Update" : "Save Changes"}
                      </Button>
                      <Button
                        onClick={handleInviteCollaborator}
                        className="flex items-center justify-center gap-2 h-10 sm:h-auto hover:opacity-90 hover:scale-105 transition-all duration-300"
                        style={{
                          background: "transparent",
                          color: "#6b2cfe",
                          border: "1px solid #6b2cfe",
                        }}
                      >
                        <UsersIcon className="w-4 h-4" />
                        Undang Kolaborasi
                      </Button>
                    </div>

                    {/* TAMBAHAN: Comment Section di Edit Mode */}
                    {currentLessonPlanId && (
                      <CommentSection
                        lessonPlanId={currentLessonPlanId}
                        onLoadComplete={() => setIsCommentsLoading(false)}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </main>

      <EnhancePromptDialog
        isOpen={showEnhancePromptDialog}
        onClose={handleCloseEnhancePromptDialog}
        currentPrompt={`${formData.topik} - ${
          selectedLearningGoals.length > 0
            ? selectedLearningGoals.join(", ")
            : formData.tujuanPembelajaran
        }`}
        currentMataPelajaran={formData.mataPelajaran}
        currentJenjangPendidikan={formData.jenjangPendidikan}
        currentKelas={formData.kelas}
        onAccept={handleAcceptEnhancedPrompt}
      />

      <InviteCollaboratorDialog
        isOpen={showInviteCollaboratorDialog}
        onClose={handleCloseInviteCollaboratorDialog}
        onInvite={handleInviteFriend}
      />
    </div>
  );
};
